import { create } from 'zustand';
import { apiService } from '@/services/api';
import { trackUsageEvent } from '@/services/usageEvent';
import { useClubStore } from '@/stores/clubStore';
import { useMatchStore } from '@/stores/matchStore';
import {
  isClubFavoriteKey,
  isMatchFavoriteKey,
  normalizeFavoritesToConnexionCles,
} from '@/utils/favoriteKeys';
import {
  favoritesEqual,
  mergeFavorites,
  normalizeFavoriteIds,
  type FavoritesData,
} from '@/utils/favoritesJson';

const STORAGE_KEY = 'tifosi_favorites';

interface FavoritesState extends FavoritesData {
  loaded: boolean;
  syncing: boolean;
  tifosiCle: string | null;
  load: () => void;
  syncWithServer: (tifosiCle: string) => Promise<void>;
  toggleMatch: (matchCle: string) => void;
  toggleClub: (clubCle: string) => void;
  isMatchFavorite: (key: string) => boolean;
  isClubFavorite: (key: string) => boolean;
}

function readStorage(): FavoritesData {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { matches: [], clubs: [] };
    const parsed = JSON.parse(raw) as Partial<FavoritesData>;
    return {
      matches: normalizeFavoriteIds(parsed.matches),
      clubs: normalizeFavoriteIds(parsed.clubs),
    };
  } catch {
    return { matches: [], clubs: [] };
  }
}

function writeStorage(data: FavoritesData): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Favorites save error:', error);
  }
}

function applyFavorites(
  set: (partial: Partial<FavoritesState>) => void,
  get: () => FavoritesState,
  data: FavoritesData,
): void {
  set({
    matches: data.matches,
    clubs: data.clubs,
    loaded: true,
  });
  writeStorage(data);
}

function normalizeWithCatalog(data: FavoritesData): FavoritesData {
  const clubs = useClubStore.getState().clubs;
  const matches = useMatchStore.getState().matches;
  if (clubs.length === 0 && matches.length === 0) return data;
  return normalizeFavoritesToConnexionCles(data, clubs, matches);
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;
let pendingSave: { tifosiCle: string; data: FavoritesData } | null = null;

async function flushPendingSave(): Promise<void> {
  const pending = pendingSave;
  pendingSave = null;
  saveTimer = null;
  if (!pending) return;
  try {
    await apiService.saveFavorites(pending.tifosiCle, pending.data);
  } catch (error) {
    console.error('Favorites server save error:', error);
  }
}

function scheduleServerSave(tifosiCle: string, data: FavoritesData): void {
  pendingSave = { tifosiCle, data };
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    void flushPendingSave();
  }, 400);
}

async function persistFavoriteToggle(
  tifosiCle: string,
  kind: 'club' | 'match',
  connexionCle: string,
  added: boolean,
): Promise<void> {
  try {
    if (kind === 'club') {
      if (added) await apiService.addFavoriteClub(tifosiCle, connexionCle);
      else await apiService.removeFavoriteClub(tifosiCle, connexionCle);
      trackUsageEvent({
        type: added ? 'club_favorited' : 'club_unfavorited',
        tifosi: tifosiCle,
        club: connexionCle,
      });
    } else {
      if (added) await apiService.addFavoriteMatch(tifosiCle, connexionCle);
      else await apiService.removeFavoriteMatch(tifosiCle, connexionCle);
      trackUsageEvent({
        type: added ? 'match_favorited' : 'match_unfavorited',
        tifosi: tifosiCle,
        match_football: connexionCle,
      });
    }
  } catch (error) {
    console.error('Favorites row save error:', error);
  }
}

export const useFavoritesStore = create<FavoritesState>((set, get) => ({
  matches: [],
  clubs: [],
  loaded: false,
  syncing: false,
  tifosiCle: null,

  load: () => {
    if (get().loaded) return;
    applyFavorites(set, get, normalizeWithCatalog(readStorage()));
  },

  syncWithServer: async (tifosiCle: string) => {
    const cle = String(tifosiCle || '').trim();
    if (!cle) return;
    if (get().syncing && get().tifosiCle === cle) return;

    set({ syncing: true, tifosiCle: cle });
    const local = normalizeWithCatalog(readStorage());

    try {
      const remote = normalizeWithCatalog(await apiService.getFavorites(cle));
      const merged = normalizeWithCatalog(mergeFavorites(local, remote));
      applyFavorites(set, get, merged);

      if (!favoritesEqual(merged, remote)) {
        await apiService.saveFavorites(cle, merged);
      }
    } catch (error) {
      console.error('Favorites sync error:', error);
      applyFavorites(set, get, local);
    } finally {
      set({ syncing: false, tifosiCle: cle, loaded: true });
    }
  },

  toggleMatch: (matchCle: string) => {
    const key = String(matchCle || '').trim();
    if (!key) return;
    const current = get().matches;
    const lookup = normalizeFavoritesToConnexionCles(
      { matches: current, clubs: [] },
      [],
      useMatchStore.getState().matches,
    ).matches;
    const normalizedCurrent = normalizeFavoriteIds(lookup);
    const added = !normalizedCurrent.includes(key);
    const nextMatches = added
      ? [...normalizedCurrent.filter((m) => m !== key), key]
      : normalizedCurrent.filter((m) => m !== key);
    const data: FavoritesData = {
      matches: nextMatches,
      clubs: get().clubs,
    };
    applyFavorites(set, get, data);
    const { tifosiCle } = get();
    if (tifosiCle) {
      if (apiService.isFavoritesRelational()) {
        void persistFavoriteToggle(tifosiCle, 'match', key, added);
      } else {
        scheduleServerSave(tifosiCle, data);
      }
    }
  },

  toggleClub: (clubCle: string) => {
    const key = String(clubCle || '').trim();
    if (!key) return;
    const current = get().clubs;
    const lookup = normalizeFavoritesToConnexionCles(
      { matches: [], clubs: current },
      useClubStore.getState().clubs,
      [],
    ).clubs;
    const normalizedCurrent = normalizeFavoriteIds(lookup);
    const added = !normalizedCurrent.includes(key);
    const nextClubs = added
      ? [...normalizedCurrent.filter((c) => c !== key), key]
      : normalizedCurrent.filter((c) => c !== key);
    const data: FavoritesData = {
      matches: get().matches,
      clubs: nextClubs,
    };
    applyFavorites(set, get, data);
    const { tifosiCle } = get();
    if (tifosiCle) {
      if (apiService.isFavoritesRelational()) {
        void persistFavoriteToggle(tifosiCle, 'club', key, added);
      } else {
        scheduleServerSave(tifosiCle, data);
      }
    }
  },

  isMatchFavorite: (key: string) =>
    isMatchFavoriteKey(get().matches, key, useMatchStore.getState().matches),
  isClubFavorite: (key: string) =>
    isClubFavoriteKey(get().clubs, key, useClubStore.getState().clubs),
}));
